import { useMemo } from "react";
import { Link } from "react-router-dom";
import IconHome from "../../../../styles/icons/IconHome";
import { getLeadRatingText } from "../../../../utils/getLeadRatingText";
import LoaderHome from "../../../atoms/Loader-home/Loader-home";
import useUserRole from "../../../../hooks/useUserRole";
import { UserRole } from "../../../../utils";

interface Props {
  allLeads?: any[];
  loading?: string;
}

interface LeadItem {
  id: string | number;
  name: string;
  email: string;
  rating: string;
  company: any;
  createdAt: string;
}

interface GroupedLead {
  date: string;
  label: string;
  data: LeadItem[];
}

const RecentLeads = ({ allLeads = [], loading = "" }: Props) => {
  const getStatusClass = (status: string) => {
    switch (status) {
      case "High Intent":
        return "chip chip--soft chip--status-hot";
      case "Low Intent":
        return "chip chip--soft chip--status-interested";
      case "Medium Intent":
        return "chip chip--soft chip--status-warm";
      case "Rejected":
        return "chip chip--soft chip--status-rejected";
      case "system error":
        return "chip chip--soft chip--status-systemerror";
      case "in progress":
        return "chip chip--soft chip--status-progress";
      default:
        return "chip chip--soft chip--status-progress";
    }
  };

  const getLeadRatingIcon = (rating: string) => {
    switch (rating) {
      case "High Intent":
        return <IconHome breadcrumb="hotLeads" />;
      case "Low Intent":
        return <IconHome breadcrumb="interestedLeads" />;
      case "Medium Intent":
        return <IconHome breadcrumb="warmLeads" />;
      case "Rejected":
        return <IconHome breadcrumb="rejectedLeads" />;
      case "system error":
        return <IconHome breadcrumb="systemError" />;
      case "in progress":
        return <IconHome breadcrumb="inProgressLeads" />;
      default:
        return <IconHome breadcrumb="inProgressLeads" />;
    }
  };

  const groupedLeads: GroupedLead[] = useMemo(() => {
    const recentEightLeads = [...allLeads]
      .sort((a: any, b: any) => {
        const parseBackendDate = (value: string) => {
          if (!value) return 0;

          const parts = value.split("/");
          if (parts.length === 3) {
            const [day, month, year] = parts.map(Number);
            return new Date(year, month - 1, day).getTime();
          }

          const time = new Date(value).getTime();
          return Number.isNaN(time) ? 0 : time;
        };

        return parseBackendDate(b.createdAt) - parseBackendDate(a.createdAt);
      })
      .slice(0, 5);

    const groupedMap = new Map<string, LeadItem[]>();

    recentEightLeads.forEach((lead: any) => {
      const rawDate = lead.createdAt || "Unknown Date";
      const dateKey = rawDate;

      const mappedLead: LeadItem = {
        id: lead.id,
        name: `${lead.firstName || ""} ${lead.lastName || ""}`.trim(),
        email: lead.email || "",
        rating: lead.rating || "",
        company: lead.assignedUsers?.[0] || {},
        createdAt: rawDate,
      };

      if (!groupedMap.has(dateKey)) {
        groupedMap.set(dateKey, []);
      }

      groupedMap.get(dateKey)?.push(mappedLead);
    });

    return Array.from(groupedMap.entries()).map(([date, data]) => ({
      date,
      label: date,
      data,
    }));
  }, [allLeads]);

  const currentUserRole = useUserRole();

  return (
    <div className="recent-leads">
      <div className="recent-leads__header">
        <h3 className="recent-leads__title">Recent Leads</h3>
        <Link to="/leads/all-leads" className="recent-leads__view-all">
          View All
        </Link>
      </div>

      <div className="recent-leads__body">
        {loading === "loading" ? (
          <div className="recent-leads__loader">
            <LoaderHome />
            <LoaderHome />
            <LoaderHome />
          </div>
        ) : groupedLeads.length === 0 ? (
          <div className="recent-leads__empty">
            <h4>No leads show</h4>
            <p>
              Your most recent lead activity will appear here once leads have
              been created
            </p>
            {currentUserRole === UserRole.Operator && (
              <Link to="/leads/new-lead" className="recent-leads__empty-link">
                Create your first lead
              </Link>
            )}
          </div>
        ) : (
          groupedLeads.map((group, groupIndex) => (
            <div key={groupIndex} className="recent-leads__section">
              <div className="recent-leads__date">{group.label}</div>

              <div className="recent-leads__list">
                {group.data.map((lead, index) => (
                  <div
                    key={`${lead.id}-${index}`}
                    className="recent-leads__card"
                  >
                    <div className="recent-leads__card-top">
                      <div className="recent-leads__company">
                        {lead?.company?.companyName} |{" "}
                        {lead?.company?.firstName} {lead?.company?.lastName}
                      </div>
                    </div>

                    <div className="recent-leads__card-middle">
                      <div className="recent-leads__avatar">
                        {getLeadRatingIcon(lead.rating)}
                      </div>

                      <div className="recent-leads__info">
                        <div className="recent-leads__name">{lead.name}</div>
                        <div className="recent-leads__email">{lead.email}</div>
                      </div>
                    </div>

                    <div className="recent-leads__card-bottom">
                      <Link
                        to={`/leads/all-leads/${lead.id}`}
                        className="recent-leads__details"
                      >
                        View Details{" "}
                        <span className="recent-leads__arrow">→</span>
                      </Link>

                      <span className={getStatusClass(lead.rating)}>
                        {getLeadRatingText(lead.rating)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RecentLeads;
